import { computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { SnackbarFactory } from '@/features/shell'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { Id } from '@/infra/id'
import type { DraftJobStore } from '@/features/jobs/drafting/DraftJobStore'
import type { PromptArchiveJobOptions } from '@/features/jobs'

/**
 * View model for the archive job confirmation dialog.
 * Handles fetching job details and archiving jobs with proper loading states
 * and user feedback via snackbars.
 */
export class ArchiveJobDialogViewModel {
  /**
   * Async prompt.
   */
  private readonly _prompt

  /**
   * ID of the job to archive.
   */
  private _id: Id = Id.none

  /**
   * Reactive getter for the display name of the job.
   */
  @observable private accessor _getDisplayName: () => string = () => ''

  /**
   * View model for the underlying modal.
   */
  readonly modal = new ModalViewModel()

  constructor(
    private readonly _newSnackbar: SnackbarFactory,
    private readonly _draftJobStore: DraftJobStore,
  ) {
    this._prompt = new AsyncPrompt<boolean>({
      abort$: this.modal.close$,
    })
  }

  /**
   * Gets the display name for the job.
   */
  @computed get displayName() {
    return this._getDisplayName()
  }

  /**
   * Prompts the user to confirm job archival.
   * @returns Promise resolving to true if archived, false if canceled
   */
  async prompt({ displayName, id, signal }: PromptArchiveJobOptions) {
    this.modal.close()

    const responsePromise = this._prompt.prompt(signal)

    runInAction(() => {
      this._id = id
      this._getDisplayName = displayName
      this.modal.show()
    })

    const response = await responsePromise
    this.modal.close()
    return response ?? false
  }

  /**
   * Cancels the dialog without archiving.
   */
  cancel() {
    this.modal.close()
  }

  /**
   * Archives the job and shows appropriate feedback.
   * Shows a pending snackbar during the operation and updates it
   * to success or error based on the result.
   */
  archive = task.resolved(async () => {
    if (!this._id) {
      return
    }

    const snackbar = this._newSnackbar((s) =>
      s
        .saying('Please wait', 'The job is being deleted.')
        .type('pending')
        .show(),
    )

    try {
      await this._draftJobStore.archive(this._id)

      snackbar.update((s) =>
        s
          .saying('Success!', 'The job has been deleted.')
          .type('success')
          .autoDismiss(),
      )

      this._prompt.respond(true)
      this.modal.close()
    } catch (error) {
      snackbar.fromError(error)
      throw error
    }
  })
}
